import { isValidSimulatorURL } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  FormInput,
  Image,
  ListItem,
  Text,
  UnorderedList,
  Video
} from "@prismicio/editor-ui"
import { type ClipboardEvent, type PropsWithChildren, useState } from "react"

import {
  simulatorScreenshotUrl,
  simulatorSetupVideoUrl
} from "../../app/constants"
import { setSimulatorURL, useRepository } from "../../app/services/repository"
import { trackSimulatorUrlSetup } from "../../app/services/tracking"
import { autoCompleteSimulatorURL, updateSimulatorURL } from "../simulatorData"
import { useSettingsMenu } from "./SettingsMenuProvider"

export function SimulatorURLDialog() {
  const settingsMenu = useSettingsMenu()

  const repository = useRepository()
  const { simulatorUrl = "" } = repository

  const [url, setUrl] = useState(simulatorUrl)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(false)

  useOnChange(settingsMenu.dialog, () => {
    setFailed(false)
    settingsMenu.dialog === "livePreview" && setUrl(simulatorUrl)
  })

  function onSave() {
    if (url === "") return

    setLoading(true)
    trackSimulatorUrlSetup({ url })

    return setSimulatorURL(url)
      .then(() => {
        updateSimulatorURL(repository, url)
        onSuccess()
      })
      .catch(onFailed)
      .finally(() => setLoading(false))
  }

  function onSuccess() {
    settingsMenu.closeDialog()
    setFailed(false)
  }

  function onFailed() {
    setFailed(true)
  }

  function onPaste(evt: ClipboardEvent<HTMLInputElement>) {
    const pastedText = evt.clipboardData.getData("text")

    if (!pastedText) return

    evt.preventDefault()
    setUrl(autoCompleteSimulatorURL(pastedText))
  }

  function onOpenChange(open: boolean) {
    if (!open) settingsMenu.closeDialog()
  }

  return (
    <Dialog
      open={settingsMenu.dialog === "livePreview"}
      onOpenChange={onOpenChange}
      size="small"
    >
      <DialogHeader icon="preview" title="Get the live editing experience" />
      <DialogContent>
        <Box overflowY="auto" flexDirection="column" padding={32} gap={16}>
          <Text variant="h3">Add your URL to setup live editing. </Text>

          <Box gap={8} alignItems="center">
            <FormInput
              type="text"
              placeholder="https://www.your-website-url.com/slice-simulator"
              value={url}
              error={failed}
              sx={{ flexGrow: 1 }}
              onValueChange={(value) => {
                setUrl(value)
                setFailed(false)
              }}
              onPaste={onPaste}
            />
            <Button
              disabled={!isValidSimulatorURL(url)}
              size="large"
              loading={loading}
              onClick={onSave}
            >
              Save
            </Button>
          </Box>

          <Text>
            Add the URL of your website in the input above followed by
            /slice-simulator for example, prismic.io/
            <Text variant="bold" component="span">
              slice-simulator.
            </Text>
          </Text>

          <Video
            src={simulatorSetupVideoUrl}
            borderRadius={4}
            sizing="contain"
            loop
          />

          <Text variant="h4" sx={{ marginTop: 16 }}>
            What if I can’t access this page?
          </Text>

          <UnorderedList sx={{ marginLeft: 8 }}>
            <ListItem>
              Check you have used the correct URL and that it ends in
              slice-simulator. You can test for yourself by going to the URL{" "}
              <HighlightedText>
                your-website.com/slice-simulator
              </HighlightedText>
              .
            </ListItem>
          </UnorderedList>

          <Image
            src={simulatorScreenshotUrl}
            alt="Slice Simulator Screenshot"
          />

          <UnorderedList sx={{ marginLeft: 8 }}>
            <ListItem>
              Check with your dev that the Slice Simulator is deployed (You can{" "}
              <Text href="https://prismic.io/docs/page-builder#prepare-your-codebase">
                share this documentation article
              </Text>{" "}
              with your developer to get live editing setup)
            </ListItem>
            <ListItem>
              In order to do this check the{" "}
              <HighlightedText>slicemachine.config.json</HighlightedText> file
              at the root of your project. The file should include a{" "}
              <HighlightedText>"localSliceSimulatorURL"</HighlightedText>
              property like{" "}
              <HighlightedText>
                "https://localhost:3000/slice-simulator"
              </HighlightedText>
              . Your project should include a corresponding page component.
            </ListItem>
          </UnorderedList>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

function HighlightedText(props: PropsWithChildren) {
  return (
    <Text color="purple11" highlight>
      {props.children}
    </Text>
  )
}
